import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, delay, forkJoin, of, catchError } from 'rxjs';
import { IAnalyticsService } from './analytics-service.interface';
import { 
  OrganizationSummaryInsight, 
  ProviderVolumeMetric, 
  FacilityVolumeMetric,
  ParameterizedReport,
  DashboardParameters
} from '../models/analytics.model';

@Injectable({
  providedIn: 'root'
})
export class MockAnalyticsService implements IAnalyticsService {
  private http = inject(HttpClient);
  private readonly basePath = 'assets/mock-data';
  private readonly networkDelay = 500;

  getDailySummary(date: string): Observable<OrganizationSummaryInsight> {
    return this.http.get<OrganizationSummaryInsight>(`${this.basePath}/daily/${date}/summary.json`)
      .pipe(delay(this.networkDelay));
  }

  getDailySummaryRange(dates: string[]): Observable<OrganizationSummaryInsight[]> {
    if (!dates || dates.length === 0) return of([]);
    const requests = dates.map(date => 
      this.getDailySummary(date).pipe(
        catchError(() => of(null as unknown as OrganizationSummaryInsight))
      )
    );
    return forkJoin(requests).pipe(
      delay(this.networkDelay)
    );
  }

  getMonthlySummary(date: string): Observable<OrganizationSummaryInsight> {
    const formattedDate = this.formatMonthlyDate(date);
    return this.http.get<OrganizationSummaryInsight>(`${this.basePath}/monthly/${formattedDate}/summary.json`)
      .pipe(delay(this.networkDelay));
  }

  getMonthlySummaryRange(months: string[]): Observable<OrganizationSummaryInsight[]> {
    if (!months || months.length === 0) return of([]);
    const requests = months.map(month => 
      this.getMonthlySummary(month).pipe(
        catchError(() => of(null as unknown as OrganizationSummaryInsight))
      )
    );
    return forkJoin(requests).pipe(
      delay(this.networkDelay)
    );
  }

  getDailyProviderVolume(date: string, providerId: string): Observable<ProviderVolumeMetric> {
    return this.http.get<ProviderVolumeMetric>(`${this.basePath}/daily/${date}/provider-volume/${providerId}.json`)
      .pipe(delay(this.networkDelay));
  }

  getMonthlyProviderVolume(date: string, providerId: string): Observable<ProviderVolumeMetric> {
    const formattedDate = this.formatMonthlyDate(date);
    return this.http.get<ProviderVolumeMetric>(`${this.basePath}/monthly/${formattedDate}/provider-volume/${providerId}.json`)
      .pipe(delay(this.networkDelay));
  }

  getDailyFacilityVolume(date: string, facilityId: string): Observable<FacilityVolumeMetric> {
    return this.http.get<FacilityVolumeMetric>(`${this.basePath}/daily/${date}/facility-volume/${facilityId}.json`)
      .pipe(delay(this.networkDelay));
  }

  getMonthlyFacilityVolume(date: string, facilityId: string): Observable<FacilityVolumeMetric> {
    const formattedDate = this.formatMonthlyDate(date);
    return this.http.get<FacilityVolumeMetric>(`${this.basePath}/monthly/${formattedDate}/facility-volume/${facilityId}.json`)
      .pipe(delay(this.networkDelay));
  }

  getParameterizedReport(date: string, isMonthly?: boolean): Observable<ParameterizedReport> {
    if (isMonthly) {
      const formattedDate = this.formatMonthlyDate(date);
      return this.http.get<ParameterizedReport>(`${this.basePath}/monthly/${formattedDate}/parameterized-report/data.json`)
        .pipe(delay(this.networkDelay));
    } else {
      return this.http.get<ParameterizedReport>(`${this.basePath}/daily/${date}/parameterized-report/data.json`)
        .pipe(delay(this.networkDelay));
    }
  }

  getParameterizedReportRange(dates: string[], isMonthly?: boolean): Observable<ParameterizedReport[]> {
    if (!dates || dates.length === 0) return of([]);
    const requests = dates.map(date => 
      this.getParameterizedReport(date, isMonthly).pipe(
        catchError(() => of(null as unknown as ParameterizedReport))
      )
    );
    return forkJoin(requests).pipe(
      delay(this.networkDelay)
    );
  }

  /**
   * Ensures the date is in YYYY-MM-01 format for monthly queries.
   */
  private formatMonthlyDate(date: string): string {
    const parts = date.split('-');
    if (parts.length >= 2) {
      return `${parts[0]}-${parts[1]}-01`;
    }
    return date;
  }

  getDashboardParameters(): Observable<DashboardParameters> {
    return this.http.get<DashboardParameters>(`${this.basePath}/metadata/parameters.json`)
      .pipe(delay(this.networkDelay));
  }
}
